import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Trophy, Heart, MessageCircle, Eye, Share2, Sparkles, ChevronRight, Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same tier as backend _RANK_REWARDS
const RANK_COINS = [100, 75, 50, 40, 30, 25, 20, 15, 10, 5];

/** Weekly Top-10 leaderboard of the most-engaged posts. Public feed, no auth needed. */
export default function TopWeeklyPosts({ limit = 10 }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  const navigate = useNavigate();
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/posts/top-weekly?limit=${limit}`);
        if (!cancel) setPosts(r.data.posts || []);
      } catch {
        // silent — component just hides if backend fails
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [limit]);

  if (loading || posts.length === 0) return null;

  const scrollTo = (id) => {
    const el = document.querySelector(`[data-testid="post-${id}"]`);
    if (!el) { navigate(`/post/${id}`); return; }
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2000);
    }
  };

  return (
    <Card
      data-testid="top-weekly-posts"
      className="p-4 overflow-hidden relative border-primary/40 bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent"
    >
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-amber-500/20 blur-3xl" aria-hidden="true" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/40">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-base tracking-tight">Top da semana</h3>
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              </div>
              <div className="text-[11px] text-muted-foreground">Os {posts.length} posts mais engajados dos últimos 7 dias</div>
            </div>
          </div>
          <div
            className="hidden sm:flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-medium text-amber-700 dark:text-amber-300"
            title="Autores dos posts no Top 10 ganham moedas semanais"
            data-testid="top-weekly-reward-hint"
          >
            💰 até <span className="font-bold">+100</span> moedas
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin" data-testid="top-weekly-list">
          {posts.map((p, i) => (
            <button
              key={p.post_id}
              type="button"
              onClick={() => scrollTo(p.post_id)}
              data-testid={`top-weekly-item-${p.post_id}`}
              className="flex-shrink-0 w-56 text-left group relative rounded-xl border border-border bg-card hover:border-primary/50 hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden"
            >
              {/* Rank badge */}
              <div
                className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shadow-md ${
                  i === 0
                    ? "bg-gradient-to-br from-yellow-400 to-amber-600 text-white"
                    : i === 1
                    ? "bg-gradient-to-br from-slate-300 to-slate-500 text-white"
                    : i === 2
                    ? "bg-gradient-to-br from-orange-400 to-orange-700 text-white"
                    : "bg-background/90 backdrop-blur border border-border text-foreground"
                }`}
                aria-label={`Posição ${i + 1}`}
              >
                {i + 1}
              </div>

              {/* Coin reward badge — visible only on user's own posts */}
              {user?.user_id === p.user_id && RANK_COINS[i] && (
                <div
                  data-testid={`top-weekly-reward-${p.post_id}`}
                  className="absolute top-2 right-8 z-10 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-md flex items-center gap-0.5"
                  title="Bônus semanal por essa posição"
                >
                  💰 +{RANK_COINS[i]}
                </div>
              )}

              {/* Thumb */}
              <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden relative">
                {p.thumb && !p.is_video && (
                  <img src={p.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                )}
                {p.is_video && (
                  <>
                    <video src={p.thumb || undefined} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-4 h-4 text-black ml-0.5" />
                      </div>
                    </div>
                  </>
                )}
                {!p.thumb && (
                  <div className="text-xs text-muted-foreground px-3 text-center line-clamp-3">
                    {(p.content || "").slice(0, 80) || "Sem prévia"}
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-2.5 space-y-2">
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/user/${p.user_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 hover:underline"
                  >
                    <Avatar className="w-5 h-5">
                      <AvatarImage src={p.author_picture} />
                      <AvatarFallback className="text-[9px]">{p.author_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium truncate max-w-[10rem]">{p.author_name}</span>
                  </Link>
                </div>
                {p.content && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{p.content}</p>
                )}
                <div className="flex items-center justify-between gap-1 pt-1 border-t border-border text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-0.5" title="Curtidas + reações"><Heart className="w-3 h-3" />{p.likes_count + p.reactions_total}</span>
                  <span className="flex items-center gap-0.5" title="Comentários"><MessageCircle className="w-3 h-3" />{p.comments_count}</span>
                  <span className="flex items-center gap-0.5" title="Compartilhamentos"><Share2 className="w-3 h-3" />{p.share_count}</span>
                  <span className="flex items-center gap-0.5" title="Visualizações"><Eye className="w-3 h-3" />{p.views}</span>
                </div>
              </div>

              {/* Arrow on hover */}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}
